import {
  useRealtimeChannelEvents,
  useRealtimePresenceChannel,
} from '@/hooks/useRealtimeChannelEvents';
import { AuthContext } from '@/providers/auth';
import { useRealtimeManager } from '@/providers/realtime';
import { useCallback, useContext, useEffect, useMemo } from 'react';

interface UsePresenceOptions {
  channelName: string;
  statusEvents?: string[];
  whisperEventName?: string;
  useClientWhispers?: boolean;
}

type ClientState = {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  online_at: string;
  typing?: boolean;
} & Record<string, any>;

type MembersMap = Record<string, ClientState>;

export const usePresence = ({
  channelName,
  statusEvents = ['status', '.status'],
  whisperEventName = 'status',
  useClientWhispers = true,
}: UsePresenceOptions) => {
  const { user } = useContext(AuthContext);
  const { manager } = useRealtimeManager();
  const { members } = useRealtimePresenceChannel(channelName, {
    enabled: Boolean(channelName),
  });
  const setClientStatus = useCallback(
    (updates: Record<string, any> = {}): ClientState => {
      return {
        ...updates,
        id: user?.id.toString() || crypto.randomUUID(),
        full_name: user?.full_name || 'Guest',
        first_name: user?.first_name || 'Guest',
        last_name: user?.last_name || '',
        online_at: new Date().toISOString(),
      };
    },
    [user],
  );

  useRealtimeChannelEvents<ClientState>({
    kind: 'presence',
    channelName,
    enabled: Boolean(channelName),
    events: statusEvents,
    handler: (payload) => {
      if (!payload?.id) {
        return;
      }

      manager.mergePresenceMember(channelName, payload);
    },
  });

  useEffect(() => {
    if (!useClientWhispers || !channelName) {
      return;
    }

    return manager.subscribeToPresenceWhisper<ClientState>({
      channelName,
      whisperName: whisperEventName,
      handler: (payload) => {
        if (!payload?.id) {
          return;
        }

        manager.mergePresenceMember(channelName, payload);
      },
    });
  }, [channelName, manager, useClientWhispers, whisperEventName]);

  const updateStatus = useCallback(
    (newState: Record<string, any>) => {
      const payload = setClientStatus(newState);
      manager.mergePresenceMember(channelName, payload);

      if (useClientWhispers) {
        manager.sendPresenceWhisper(channelName, whisperEventName, payload);
      }
    },
    [channelName, manager, setClientStatus, useClientWhispers, whisperEventName],
  );

  const leave = useCallback(() => {
    manager.leaveChannel('presence', channelName);
  }, [channelName, manager]);

  const membersById = useMemo(() => members as MembersMap, [members]);
  const membersList = useMemo(() => Object.values(membersById), [membersById]);

  return {
    channel: null,
    members: membersById,
    here: membersList,
    updateStatus,
    leave,
  };
};
